import { useState, useEffect } from "react";
import axios from "axios";
import { Activity } from "lucide-react";

// Backend URL: in produzione usa window.location.origin (bundle portable), in dev l'env.
const BACKEND_URL = (typeof window !== 'undefined' && process.env.NODE_ENV === 'production')
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${BACKEND_URL}/api`;

// ==================== Monitor Banda (traffico proxy VPS) ====================
const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b >= 1e12) return `${(b / 1e12).toFixed(2)} TB`;
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
};
const fmtDur = (s) => (s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m` : s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

const BandwidthPage = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/admin/bandwidth`)
      .then((r) => { if (alive) { setData(r.data); setLoadError(false); } })
      .catch((e) => { console.warn("monitor banda non raggiungibile", e); if (alive) setLoadError(true); });
    load();
    const iv = setInterval(load, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const maxDay = Math.max(1, ...(data?.last7 || []).map((d) => d.bytes));

  return (
    <div className="space-y-6" data-testid="bandwidth-page">
      <div>
        <h1 className="font-headings text-4xl mb-1">Monitor Banda</h1>
        <p className="text-zinc-500 text-sm">
          Solo il traffico instradato via VPS (proxy). Il traffico diretto al provider non tocca la VPS. Aggiornato ogni 5s.
        </p>
        {loadError && (
          <p className="text-amber-400 text-sm mt-2" data-testid="bw-load-error">
            Impossibile aggiornare i dati del monitor: riprovo automaticamente…
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="bw-active-card">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">In onda ora (via VPS)</p>
          <p className="text-3xl font-bold">{data ? data.active.length : "—"}</p>
          <p className="text-xs text-zinc-500 mt-1">stream proxati attivi</p>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="bw-mbps-card">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Banda VPS istantanea</p>
          <p className={`text-3xl font-bold ${data && data.active_total_mbps > 700 ? "text-netflix-red" : data && data.active_total_mbps > 400 ? "text-amber-400" : "text-green-500"}`}>
            {data ? data.active_total_mbps : "—"} <span className="text-base text-zinc-500">Mbps</span>
          </p>
          <p className="text-xs text-zinc-500 mt-1">su 1000 Mbps disponibili</p>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="bw-today-card">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Traffico VPS oggi</p>
          <p className="text-3xl font-bold">{data ? fmtBytes(data.today.total_bytes) : "—"}</p>
          <p className="text-xs text-zinc-500 mt-1">totale relayed dal proxy</p>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
        <h2 className="font-bold mb-4 flex items-center gap-2"><Activity className="w-4 h-4 text-netflix-red" /> Stream attivi via VPS</h2>
        {(data?.active || []).length === 0 ? (
          <p className="text-zinc-500 text-sm py-4" data-testid="bw-no-active">
            Nessuno stream sta passando dalla VPS in questo momento (tutti i clienti sono in collegamento diretto).
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm" data-testid="bw-active-table">
              <thead className="text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="text-left py-2">Cliente</th>
                  <th className="text-left py-2">Tipo</th>
                  <th className="text-left py-2">Durata</th>
                  <th className="text-left py-2">Dati</th>
                  <th className="text-left py-2">Velocità</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.active.map((s) => (
                  <tr key={`${s.user_id}-${s.kind}-${s.stream_id}`}>
                    <td className="py-2 font-medium">{s.name}</td>
                    <td className="py-2 text-zinc-400">{s.kind === "live" ? "Canale TV" : s.kind === "movie" ? "Film" : "Serie"}</td>
                    <td className="py-2 text-zinc-400">{fmtDur(s.seconds)}</td>
                    <td className="py-2">{fmtBytes(s.bytes)}</td>
                    <td className="py-2 font-mono">{s.mbps} Mbps</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
          <h2 className="font-bold mb-4">Consumo di oggi per cliente</h2>
          {(data?.today?.per_user || []).length === 0 ? (
            <p className="text-zinc-500 text-sm py-4">Nessun consumo VPS registrato oggi.</p>
          ) : (
            <div className="space-y-2" data-testid="bw-per-user">
              {data.today.per_user.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
                  <span className="text-sm font-medium truncate mr-3">{u.name}</span>
                  <span className="text-sm font-mono text-zinc-300">{fmtBytes(u.bytes)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
          <h2 className="font-bold mb-4">Ultimi 7 giorni</h2>
          <div className="flex items-end gap-2 h-40" data-testid="bw-last7">
            {(data?.last7 || []).map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-zinc-400">{d.bytes ? fmtBytes(d.bytes) : ""}</span>
                <div
                  className={`w-full rounded-t-sm transition-all ${d.bytes ? "bg-netflix-red/70" : "bg-zinc-800"}`}
                  style={{ height: d.bytes ? `${Math.max(6, (d.bytes / maxDay) * 100)}%` : "6px" }}
                />
                <span className="text-[10px] text-zinc-500">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BandwidthPage;
